import { MediaitemType, MediaitemUsageType } from './mediaitems';

enum BackgroundActivityKind {
  UPLOAD = 'UPLOAD'
}

enum BackgroundActivityStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

enum BackgroundActivityTargetKind {
  TASK_BLOCK_CONTENT = 'TASK_BLOCK_CONTENT',
  TASK_BLOCK_FILE_SLOT = 'TASK_BLOCK_FILE_SLOT',
  TASK_BLOCK_STATIC_ATTACHMENTS = 'TASK_BLOCK_STATIC_ATTACHMENTS',
  ASSIGNMENT_BLOCK_FILE_SLOT = 'ASSIGNMENT_BLOCK_FILE_SLOT',
  GOAL_COVER = 'GOAL_COVER',
  GOAL_LANDING_PAGE_HERO = 'GOAL_LANDING_PAGE_HERO',
  GOAL_LANDING_PAGE_MENTOR_BIO = 'GOAL_LANDING_PAGE_MENTOR_BIO',
  USER_PROFILE_PICTURE = 'USER_PROFILE_PICTURE',
  MENTOR_ATTACHMENT = 'MENTOR_ATTACHMENT'
}

type BackgroundJobActivityTarget =
  | {
      kind: BackgroundActivityTargetKind.TASK_BLOCK_CONTENT;
      taskEntityId: string;
      blockEntityId: string;
      contentItemCid?: string;
    }
  | {
      kind: BackgroundActivityTargetKind.TASK_BLOCK_FILE_SLOT;
      taskEntityId: string;
      blockEntityId: string;
      fileId: string;
    }
  | {
      kind: BackgroundActivityTargetKind.TASK_BLOCK_STATIC_ATTACHMENTS;
      taskEntityId: string;
      blockEntityId: string;
    }
  | {
      kind: BackgroundActivityTargetKind.ASSIGNMENT_BLOCK_FILE_SLOT;
      subscriptionId: number;
      taskEntityId: string;
      blockEntityId: string;
      fileId: string;
    }
  | { kind: BackgroundActivityTargetKind.GOAL_COVER; goalEntityId: string }
  | { kind: BackgroundActivityTargetKind.GOAL_LANDING_PAGE_HERO; goalEntityId: string }
  | {
      kind: BackgroundActivityTargetKind.GOAL_LANDING_PAGE_MENTOR_BIO;
      goalEntityId: string;
      mentorIndex: number;
    }
  | { kind: BackgroundActivityTargetKind.USER_PROFILE_PICTURE }
  | { kind: BackgroundActivityTargetKind.MENTOR_ATTACHMENT; conversationId: number };

interface BaseBackgroundActivity {
  id: string;
  kind: BackgroundActivityKind;
  targetKey: string;
  target: BackgroundJobActivityTarget;
  status: BackgroundActivityStatus;
  progress: number | null;
  startedAt: number;
  error: string | null;
}

interface UploadActivity extends BaseBackgroundActivity {
  kind: BackgroundActivityKind.UPLOAD;
  payload: {
    filename: string;
    mediaitemType: MediaitemType | null;
    mediaitemUsageType: MediaitemUsageType;
  };
  result: {
    mediaitemId: number;
    originalFilename: string;
    contentType: string;
  } | null;
  restored?: boolean;
}

type BackgroundActivity = UploadActivity;

function isUploadActivity(activity: BackgroundActivity): activity is UploadActivity {
  return activity.kind === BackgroundActivityKind.UPLOAD;
}

function serializeActivityTarget(target: BackgroundJobActivityTarget): string {
  switch (target.kind) {
    case BackgroundActivityTargetKind.TASK_BLOCK_CONTENT: {
      // contentItemCid is assumed to contain only [a-zA-Z0-9-] (UUIDs from randomUUID, numeric
      // stringify for choices). A `.` in a cid would let two distinct targets collide on this key.
      const cidSuffix = target.contentItemCid ? `.${target.contentItemCid}` : '';
      return `task.${target.taskEntityId}.block.${target.blockEntityId}.content${cidSuffix}`;
    }
    case BackgroundActivityTargetKind.TASK_BLOCK_FILE_SLOT:
      return `task.${target.taskEntityId}.block.${target.blockEntityId}.fileSlot.${target.fileId}`;
    case BackgroundActivityTargetKind.TASK_BLOCK_STATIC_ATTACHMENTS:
      return `task.${target.taskEntityId}.block.${target.blockEntityId}.staticAttachments`;
    case BackgroundActivityTargetKind.ASSIGNMENT_BLOCK_FILE_SLOT:
      return `assignment.${target.subscriptionId}.task.${target.taskEntityId}.block.${target.blockEntityId}.fileSlot.${target.fileId}`;
    case BackgroundActivityTargetKind.GOAL_COVER:
      return `goal.${target.goalEntityId}.cover`;
    case BackgroundActivityTargetKind.GOAL_LANDING_PAGE_HERO:
      return `goal.${target.goalEntityId}.landingPage.hero`;
    case BackgroundActivityTargetKind.GOAL_LANDING_PAGE_MENTOR_BIO:
      return `goal.${target.goalEntityId}.landingPage.mentorBio.${target.mentorIndex}`;
    case BackgroundActivityTargetKind.USER_PROFILE_PICTURE:
      return `user.profilePicture`;
    case BackgroundActivityTargetKind.MENTOR_ATTACHMENT:
      return `mentor.attachment.${target.conversationId}`;
  }
}

export {
  BackgroundActivityKind,
  BackgroundActivityStatus,
  BackgroundActivityTargetKind,
  isUploadActivity,
  serializeActivityTarget
};
export type { BackgroundActivity, BackgroundJobActivityTarget, UploadActivity };
